import {
    Card, Container, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, Tooltip, Typography
} from '@mui/material';
import React, {FC, useEffect} from 'react';
import {useOktaAuth} from '@okta/okta-react';

const minRecordsPerPage = 10;
const maxRecordsPerPage = 25;
const rowsPerPageInitial = 10;

type Request = {
    domain: string;
    fname: string;
    lname: string;
    role1: string;
    role2: string;
    role3: string;
    status: string;
    studentId: string;
};

const myRequestColumns = [
    {
        studentId: 'Student Id'
    },
    {
        fname: 'First Name'
    },
    {
        lname: 'Last Name'
    },
    {
        domain: 'Domain Id'
    },
    {
        role1: 'Role 1'
    },
    {
        role2: 'Role 2'
    },
    {
        role3: 'Role 3'
    },
    {
        status: 'Status'
    }
];

export const MyAdminRequests: FC<unknown> = () => {
    const {authState} = useOktaAuth();
    const [state, setState] = React.useState({
        page: 0,
        rowsPerPage: rowsPerPageInitial,
        myAdminRequests: []
    });

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/requests`, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            }
        })
            .then((res) => res.json())
            .then((data) => {
                setState((prevState) => ({
                    ...prevState,
                    myAdminRequests: data.message
                }));
                return null;
            }).catch((error) => {
                console.log(error);
            });
    }, [authState]);

    const [sorting, setSorting] = React.useState(
        {
            key: '',
            ascending: true
        });

    const applySorting = (key: string): void => {
        let asc = true;

        if (sorting.key === key && sorting.ascending) {
            asc = false;
        }

        const currentAdminRequests = [...state.myAdminRequests];

        const sortedAdminRequests = currentAdminRequests.sort((a, b) => {
            const m: string = a[key];
            const n: string = b[key];

            return m.localeCompare(n);
        });

        setState((prevState) => ({
            ...prevState,
            myAdminRequests: asc ? sortedAdminRequests : sortedAdminRequests.reverse()
        }));

        setSorting(
            {
                key,
                ascending: asc
            }
        );
    };

    return (
        <Container
            sx={{
                paddingTop: '1rem',
                textAlign: 'left'
            }}
        >
            <Typography
                component='p'
                marginBottom='1rem'
                variant='h4'
            >
                {'My Requests'}
            </Typography>

            {state.myAdminRequests.length === 0 ?
                (
                    <Card
                        sx={{
                            width: '100%',
                            height: '6rem'
                        }}
                    >
                        <Typography
                            align={'center'}
                            component='p'
                            marginTop='2rem'
                            variant='h4'
                        >
                            {'You currently have no previous requests.'}
                        </Typography>
                    </Card>
                ) :
                (
                    <Card
                        sx={{
                            overflow: 'hidden',
                            width: '100%'
                        }}
                    >
                        <TableContainer
                            sx={{
                                maxHeight: 600
                            }}
                        >
                            <Table
                                aria-label='sticky table'
                                size='medium'
                                stickyHeader

                            >
                                <TableHead>
                                    <TableRow>
                                        {
                                            myRequestColumns.map((column, index) => (
                                                <Tooltip
                                                    key={index}
                                                    placement='top'
                                                    sx={{
                                                        height: '1rem'
                                                    }}
                                                    title='Click to sort'
                                                >
                                                    <TableCell
                                                        key={index}
                                                        onClick={(): void => applySorting(Object.keys(column)[0])}
                                                        style={{
                                                            fontSize: '1rem',
                                                            minWidth: 150
                                                        }}
                                                    >
                                                        {Object.values(column)[0]}
                                                    </TableCell>
                                                </Tooltip>))
                                        }
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        (state.rowsPerPage > 0 ?
                                            state.myAdminRequests.slice(state.page * state.rowsPerPage, state.page * state.rowsPerPage + state.rowsPerPage) :
                                            state.myAdminRequests
                                        ).map((row: Request, index) => (
                                            <TableRow
                                                hover={true}
                                                key={index}
                                            >
                                                <TableCell>
                                                    {row.studentId}
                                                </TableCell>
                                                <TableCell>
                                                    {row.fname}
                                                </TableCell>
                                                <TableCell>
                                                    {row.lname}
                                                </TableCell>
                                                <TableCell>
                                                    {row.domain}
                                                </TableCell>
                                                <TableCell>
                                                    {row.role1}
                                                </TableCell>
                                                <TableCell>
                                                    {row.role2}
                                                </TableCell>
                                                <TableCell>
                                                    {row.role3}
                                                </TableCell>
                                                <TableCell>
                                                    {row.status}
                                                </TableCell>
                                            </TableRow>
                                        )
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component={'div'}
                            count={state.myAdminRequests.length}
                            onPageChange={(event: any, newPage: any): void => {
                                setState((prevState) => ({
                                    ...prevState,
                                    page: newPage
                                }));
                            }}
                            onRowsPerPageChange={(event: any): void => {
                                setState((prevState) => ({
                                    ...prevState,
                                    page: 0,
                                    rowsPerPage: Number(event.target.value)
                                }));
                            }}
                            page={state.page}
                            rowsPerPage={state.rowsPerPage}
                            rowsPerPageOptions={[minRecordsPerPage, maxRecordsPerPage]}
                        />
                    </Card>
                )
            }
        </Container>
    );
};
